/**
 * ProjectDetailTracker
 *
 * Client-side component that fires performance-marketing events for the
 * project / development detail page.  Mirrors PropertyDetailTracker but
 * uses project-specific event names and payloads.
 *
 * Events fired:
 *  - view_item              (page load — remarketing / retargeting)
 *  - project_detail_view    (custom, richer payload for GTM)
 *  - scroll_depth           (25 / 50 / 75 / 100 %) — RR uses depth_percentage
 *  - engagement_ping        (15 / 30 / 60 / 120 s) — RR uses engaged_time_ms
 *  - section_view           (each major content section enters viewport)
 */

'use client';

import { useEffect, useRef, useCallback } from 'react';
import { analyticsEvents } from '@/components/analytics/GoogleAnalytics';
import { fireRREvent } from '@/components/analytics/RouteRoyalTracker';
import { VISITOR_ID_COOKIE } from '@/lib/attribution';

interface ProjectDetailTrackerProps {
  projectId: string;
  projectName: string;
  projectType?: string | null;
  status?: string | null;
  stage?: string | null;
  totalUnits?: number | null;
  availableUnits?: number | null;
  displayPriceText?: string | null;
  location?: string | null;
}

const SCROLL_THRESHOLDS = [25, 50, 75, 100] as const;
const ENGAGEMENT_THRESHOLDS_SEC = [15, 30, 60, 120] as const;

const SECTION_SELECTORS = [
  { id: 'overview', selector: '[data-track-section="overview"]' },
  { id: 'features', selector: '[data-track-section="features"]' },
  { id: 'gallery', selector: '[data-track-section="gallery"]' },
  { id: 'units', selector: '[data-track-section="units"]' },
  { id: 'timeline', selector: '[data-track-section="timeline"]' },
  { id: 'location', selector: '[data-track-section="location"]' },
  { id: 'contact', selector: '[data-track-section="contact"]' },
] as const;

function getVisitorId(): string | undefined {
  if (typeof document === 'undefined') return undefined;
  const match = document.cookie.match(new RegExp(`(?:^|; )${VISITOR_ID_COOKIE}=([^;]*)`));
  return match?.[1] ? decodeURIComponent(match[1]) : undefined;
}

function pushToDataLayer(payload: Record<string, unknown>) {
  if (typeof window !== 'undefined' && window.dataLayer) {
    window.dataLayer.push(payload);
  }
}

function fireGtagEvent(name: string, params: Record<string, unknown>) {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', name, params);
  }
}

export function ProjectDetailTracker({
  projectId,
  projectName,
  projectType,
  status,
  stage,
  totalUnits,
  availableUnits,
  displayPriceText,
  location,
}: ProjectDetailTrackerProps) {
  const scrollMilestones = useRef(new Set<number>());
  const engagementMilestones = useRef(new Set<number>());
  const viewedSections = useRef(new Set<string>());
  const pageLoadTime = useRef(Date.now());

  const itemPayload = useCallback(() => {
    const visitorId = getVisitorId();
    return {
      item_id: projectId,
      item_name: projectName,
      item_category: 'project',
      ...(projectType && { item_variant: projectType }),
      ...(status && { status }),
      ...(stage && { stage }),
      ...(totalUnits != null && { total_units: totalUnits }),
      ...(availableUnits != null && { available_units: availableUnits }),
      ...(displayPriceText && { display_price: displayPriceText }),
      ...(location && { location }),
      ...(visitorId && { visitor_id: visitorId }),
    };
  }, [projectId, projectName, projectType, status, stage, totalUnits, availableUnits, displayPriceText, location]);

  // Fire view events on mount
  useEffect(() => {
    analyticsEvents.viewProject(projectId, projectName, projectType ?? undefined);

    const detail = itemPayload();
    pushToDataLayer({ event: 'project_detail_view', ...detail });
    fireGtagEvent('project_detail_view', detail);
  }, []);

  // Scroll depth tracking
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      if (docHeight <= 0) return;

      const pct = Math.round((scrollTop / docHeight) * 100);

      for (const threshold of SCROLL_THRESHOLDS) {
        if (pct >= threshold && !scrollMilestones.current.has(threshold)) {
          scrollMilestones.current.add(threshold);
          const params = {
            percent_scrolled: threshold,
            depth_percentage: threshold,
            ...itemPayload(),
            event_category: 'engagement',
          };
          fireGtagEvent('scroll_depth', params);
          pushToDataLayer({ event: 'scroll_depth', ...params });
          fireRREvent('scroll_depth', {
            development_id: projectId,
            depth_percentage: threshold,
          });
        }
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [itemPayload, projectId]);

  // Engagement time tracking (RR canonical: engagement_ping + engaged_time_ms)
  useEffect(() => {
    const timers = ENGAGEMENT_THRESHOLDS_SEC.map((sec) =>
      setTimeout(() => {
        if (engagementMilestones.current.has(sec)) return;
        engagementMilestones.current.add(sec);
        const engagedTimeMs = sec * 1000;
        const params = {
          engagement_time_sec: sec,
          engaged_time_ms: engagedTimeMs,
          ...itemPayload(),
          event_category: 'engagement',
        };
        fireGtagEvent('engagement_time', params);
        pushToDataLayer({ event: 'engagement_time', ...params });
        fireRREvent('engagement_ping', {
          development_id: projectId,
          engaged_time_ms: engagedTimeMs,
        });
      }, sec * 1000),
    );

    return () => timers.forEach(clearTimeout);
  }, [itemPayload, projectId]);

  // Section visibility tracking
  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return;

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const sectionId = (entry.target as HTMLElement).dataset.trackSection;
          if (!sectionId || viewedSections.current.has(sectionId)) continue;

          viewedSections.current.add(sectionId);
          const timeOnPage = Math.round((Date.now() - pageLoadTime.current) / 1000);
          const params = {
            section_name: sectionId,
            time_to_section_sec: timeOnPage,
            ...itemPayload(),
            event_category: 'engagement',
          };
          fireGtagEvent('section_view', params);
          pushToDataLayer({ event: 'section_view', ...params });
          fireRREvent('section_view', { development_id: projectId, section_name: sectionId, time_to_section_sec: timeOnPage });
        }
      },
      { threshold: 0.3 },
    );

    const timer = setTimeout(() => {
      for (const { selector } of SECTION_SELECTORS) {
        const el = document.querySelector(selector);
        if (el) observer.observe(el);
      }
    }, 500);

    return () => {
      clearTimeout(timer);
      observer.disconnect();
    };
  }, [itemPayload]);

  return null;
}
